// TMOS Action Entity Configuration
// Date: January 11, 2026
// Purpose: Entity definition for FlexibleCRUDTable

import { EntityDefinition } from '@/types/crud';
import type { BaseEntity } from '@/components/common/CRUDTable/types';

export const actionEntity: EntityDefinition = {
  name: 'actions',
  displayName: 'Actions',
  displayNameSingular: 'Action',
  apiEndpoint: '/actions',
  idField: 'id',
  nameField: 'title',
  copyable: true,
  deletable: true,
  searchable: true,
  sortable: true,
  pageSize: 20,
  fields: [
    {
      key: 'id',
      label: 'ID',
      type: 'text',
      hidden: true,
      order: 0
    },
    {
      key: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter action title',
      order: 1
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter action description',
      order: 2
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: [
        { value: 'PENDING', label: 'Pending' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'CANCELLED', label: 'Cancelled' },
        { value: 'ON_HOLD', label: 'On Hold' }
      ],
      defaultValue: 'PENDING',
      order: 3,
      colorCoding: {
        'PENDING': 'bg-gray-100 text-gray-800',
        'IN_PROGRESS': 'bg-blue-100 text-blue-800',
        'COMPLETED': 'bg-green-100 text-green-800',
        'CANCELLED': 'bg-red-100 text-red-800',
        'ON_HOLD': 'bg-yellow-100 text-yellow-800'
      }
    },
    {
      key: 'priority',
      label: 'Priority',
      type: 'select',
      required: true,
      options: [
        { value: 'LOW', label: 'Low' },
        { value: 'MEDIUM', label: 'Medium' },
        { value: 'HIGH', label: 'High' },
        { value: 'CRITICAL', label: 'Critical' }
      ],
      defaultValue: 'MEDIUM',
      order: 4,
      colorCoding: {
        'LOW': 'bg-green-100 text-green-800',
        'MEDIUM': 'bg-yellow-100 text-yellow-800',
        'HIGH': 'bg-orange-100 text-orange-800',
        'CRITICAL': 'bg-red-100 text-red-800'
      }
    },
    {
      key: 'activity_category_id',
      label: 'Category',
      type: 'select',
      placeholder: 'Select category',
      optionsEndpoint: '/generic/activity_categories',
      optionsValueField: 'id',
      optionsLabelField: 'name',
      order: 5
    },
    {
      key: 'due_date',
      label: 'Due Date',
      type: 'date',
      placeholder: 'Select due date',
      order: 6
    }
  ],
  tableColumns: [
    {
      key: 'title',
      label: 'Title',
      sortable: true
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true
    },
    {
      key: 'assigned_to_display',
      label: 'Assigned to',
      sortable: false,
      render: (_value: unknown, item?: BaseEntity) => {
        const assigned = (item as any)?.assigned_users as Array<{ user_display?: string; user_name?: string }> | undefined;
        if (!assigned || assigned.length === 0) return '—';
        return assigned.map((u) => u.user_display || u.user_name || 'Unknown').join(', ');
      }
    },
    {
      key: 'assigned_by_display',
      label: 'Assigned by',
      sortable: false,
      render: (_value: unknown, item?: BaseEntity) => {
        const assigned = (item as any)?.assigned_users as Array<{ assigned_by_display?: string; assigned_by_name?: string }> | undefined;
        if (!assigned || assigned.length === 0) return '—';
        const names = new Set(
          assigned.map((u) => u.assigned_by_display || u.assigned_by_name || 'Unknown')
        );
        return Array.from(names).join(', ');
      }
    },
    {
      key: 'due_date',
      label: 'Due Date',
      sortable: true
    },
    {
      key: 'activity_category_name',
      label: 'Category',
      sortable: false
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true
    }
  ],
  searchFields: ['title', 'description'],
  defaultSort: { field: 'created_at', direction: 'desc' }
};

